import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import theme from '../../theme';
import { truncateText } from '../../utils/helpers';
import SyntheticAssetCreator from './SyntheticAssetCreator';
import SynthPairCreator from './SynthPairCreator';
import SyntheticTransferModal from './SyntheticTransferModal';
import AccountInstrumentsModal from './AccountInstrumentsModal';
import {
  addInstrumentToFolder,
  applyAutoFolderPlan,
  createScannerFolder,
  createScannerInstrument,
  deleteScannerFolder,
  deleteScannerInstrument,
  editScannerInstrument,
  fetchAutoFolderPlan,
  fetchScannerLibrary,
  promoteInstrumentToTrading,
  removeInstrumentFromFolder,
  renameScannerFolder,
  setInstrumentActive,
  setInstrumentBlacklist,
} from '../../services/api';

export const instrumentKey = (instrument) => (
  `${instrument.broker}-${instrument.symbol}-${instrument.sectype || ''}`
);

/** Search is on the name the operator reads on the card, not on the row behind it. */
export const matchesQuery = (instrument, query) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return instrument.symbol.toLowerCase().includes(needle);
};

/** Alphabetical by symbol; the broker only settles ties, as it does server-side. */
export const sortInstruments = (instruments, ascending) => {
  const sorted = [...instruments].sort((a, b) => (
    a.symbol.localeCompare(b.symbol, undefined, { sensitivity: 'base' })
    || a.broker.localeCompare(b.broker)
  ));
  return ascending ? sorted : sorted.reverse();
};

/**
 * The library is two lists, not one: the instruments that are working and the
 * ones that were parked. Nothing carries a blacklist flag it does not have, so
 * a synthetic or a pair belongs to the working list and only to that one.
 */
export const matchesBlacklistView = (instrument, view) => (
  view === 'blacklisted' ? instrument.is_blacklisted === true : instrument.is_blacklisted !== true
);

/** Parked instruments are noise until asked for, so the library opens on the
 *  active ones. */
export const matchesActiveView = (instrument, view) => (
  view === 'inactive' ? instrument.is_active === false : instrument.is_active !== false
);

// Which account a filed instrument's settings come from: asked when filing, and
// again when the parent stops carrying it (`stale`).
export const ParentPicker = ({
  instrument, accounts, remaining = 0, stale = null, onPick, onClose,
}) => (
  <Modal title="Which account are the settings taken from?" onClose={onClose}>
    <p className={`mb-3 text-xs ${theme.textMuted}`}>
      {stale
        ? `${instrument.symbol}: parent ${stale} no longer carries it. Pick a new one.`
        : `${instrument.symbol} lives on several accounts.`}
      {' '}Sync is switched on for the whole set, and the account you pick overwrites the others.
      {remaining > 0 && ` ${remaining} more to answer.`}
    </p>
    <div className="space-y-1">
      {accounts.map((account) => (
        <button
          key={account}
          onClick={() => onPick(account)}
          className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-olive-50"
        >
          {account}
        </button>
      ))}
    </div>
  </Modal>
);

const Modal = ({ title, onClose, wide = false, children }) => {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'} rounded-2xl bg-white p-5 shadow-xl`}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className={`text-sm font-bold ${theme.textPrimary}`}>{title}</h3>
          <button onClick={onClose} className={`text-xs ${theme.textMuted} hover:text-red-500`}>ESC</button>
        </div>
        {children}
      </div>
    </div>
  );
};

/** Name form for both creating and renaming — same field, same rules. */
const FolderNameForm = ({ initialName = '', submitLabel, onSubmit, onClose }) => {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  const submit = async (event) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    await onSubmit(trimmed);
    setSaving(false);
  };

  return (
    <form onSubmit={submit}>
      <input
        ref={inputRef}
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Folder name"
        maxLength={60}
        className="w-full rounded-lg border border-olive-200 px-3 py-2 text-sm outline-none focus:border-olive-400"
      />
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className={`rounded-lg px-3 py-1.5 text-xs font-bold ${theme.textMuted} hover:bg-olive-50`}
        >
          CANCEL
        </button>
        <button
          type="submit"
          disabled={!name.trim() || saving}
          className="rounded-lg bg-olive-500 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40"
        >
          {saving ? '…' : submitLabel}
        </button>
      </div>
    </form>
  );
};

/** Manual instrument entry: symbol plus the fields the row cannot default. */
const InstrumentForm = ({
  ibAccounts, hasOkx, defaultAccount, folders = [], defaultFolderId = null, onSubmit, onClose,
}) => {
  const [form, setForm] = useState({
    // Left blank on purpose: the operator fills what the instrument actually is,
    // and the server falls back to STK / SMART / USD for whatever stays empty.
    broker: 'ib', symbol: '', sectype: '', currency: '',
    exchange: '', account: defaultAccount || '', folderId: defaultFolderId ? String(defaultFolderId) : '',
  });
  const [saving, setSaving] = useState(false);
  const set = (field) => (event) => setForm((f) => ({ ...f, [field]: event.target.value }));
  const field = 'w-full rounded-lg border border-olive-200 px-3 py-2 text-sm outline-none focus:border-olive-400';

  const submit = async (event) => {
    event.preventDefault();
    if (!form.symbol.trim() || saving) return;
    setSaving(true);
    await onSubmit(form);
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      {hasOkx && (
        <select value={form.broker} onChange={set('broker')} className={field} aria-label="Broker">
          <option value="ib">IB</option>
          <option value="okx">OKX</option>
        </select>
      )}
      <input
        autoFocus
        value={form.symbol}
        onChange={set('symbol')}
        placeholder="Symbol"
        aria-label="Symbol"
        className={field}
      />
      {form.broker === 'ib' && (
        <>
          <input value={form.sectype} onChange={set('sectype')} placeholder="Sectype — STK if left blank" aria-label="Sectype" maxLength={7} className={field} />
          <input value={form.exchange} onChange={set('exchange')} placeholder="Exchange — SMART if left blank" aria-label="Exchange" className={field} />
          <input value={form.currency} onChange={set('currency')} placeholder="Currency — USD if left blank" aria-label="Currency" maxLength={3} className={field} />
          {/* Scanner-only by default: MONITOR is where a new instrument belongs
              until it is explicitly sent to trading. */}
          <select value={form.account} onChange={set('account')} className={field} aria-label="Account">
            {ibAccounts.map((account) => (
              <option key={account} value={account}>{account}</option>
            ))}
          </select>
        </>
      )}
      {/* A folder makes the instrument scanner-only, whatever the account. */}
      {folders.length > 0 && (
        <select value={form.folderId} onChange={set('folderId')} className={field} aria-label="Folder">
          <option value="">No folder</option>
          {folders.map((folder) => (
            <option key={folder.id} value={folder.id}>📁 {folder.name}</option>
          ))}
        </select>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${theme.textMuted} hover:bg-olive-50`}>
          CANCEL
        </button>
        <button type="submit" disabled={!form.symbol.trim() || saving} className="rounded-lg bg-olive-500 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40">
          {saving ? '…' : 'CREATE'}
        </button>
      </div>
    </form>
  );
};

/** IB securities and synthetics carry an editable contract; a pair's is its two legs. */
export const isEditable = (instrument) => (
  instrument.broker === 'ib' && ['security', 'synthetic'].includes(instrument.kind)
);

/** What already sits at the new sectype, one line each, for the operator to agree away. */
export const describeConflicts = (conflicts = {}) => [
  conflicts.accounts && `Already on ${conflicts.accounts.join(', ')} — that row is replaced`,
  ...Object.entries(conflicts.open_positions || {}).map(
    ([account, quantity]) => `OPEN POSITION on ${account}: ${quantity} — the system will stop tracking it, the broker position stays open`,
  ),
  conflicts.positions && `${conflicts.positions} position record(s) are replaced`,
  conflicts.folders && `Filed in ${conflicts.folders.join(', ')}`,
  conflicts.virtual_accounts && `Listed in virtual account ${conflicts.virtual_accounts.join(', ')}`,
  conflicts.redis_keys && `Leftover Redis keys are replaced: ${conflicts.redis_keys.join(', ')}`,
  conflicts.shared_accounts && `${conflicts.shared_accounts} other account(s) hold it — this one joins their sync set, their keys stay`,
].filter(Boolean);

/** Contract edit: blank keeps the value; a taken target is shown before it is overwritten. */
/** Fields a synthetic still lacks for its transfer, by the server's rule: blank or TBD. */
export const missingForTransfer = (form) => ['side', 'sectype', 'currency', 'exchange'].filter(
  (name) => !String(form[name] || '').trim() || String(form[name]).trim().toUpperCase() === 'TBD',
);

const EditInstrumentForm = ({ instrument, onSubmit, onClose }) => {
  const synthetic = instrument.kind === 'synthetic';
  const [form, setForm] = useState({
    sectype: instrument.sectype || '',
    currency: instrument.currency || '',
    exchange: instrument.exchange || '',
    // A security's side is its position's, so it is not on the form at all.
    ...(synthetic ? { side: ['BUY', 'SELL'].includes(instrument.side) ? instrument.side : '' } : {}),
  });
  const [conflicts, setConflicts] = useState(null);
  // What the press that raised the conflicts asked for, repeated by OVERWRITE.
  const [transferIntent, setTransferIntent] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (name) => (event) => {
    setConflicts(null);
    setForm((f) => ({ ...f, [name]: event.target.value }));
  };
  const field = 'w-full rounded-lg border border-olive-200 px-3 py-2 text-sm outline-none focus:border-olive-400';
  const missing = synthetic ? missingForTransfer(form) : [];

  const send = async (transfer) => {
    if (saving) return;
    setSaving(true);
    const result = await onSubmit(form, conflicts !== null, transfer);
    setSaving(false);
    if (result?.conflicts) {
      setConflicts(result.conflicts);
      setTransferIntent(transfer);
    }
  };
  const submit = (event) => {
    event.preventDefault();
    send(conflicts ? transferIntent : false);
  };

  return (
    <form onSubmit={submit} className="space-y-2">
      <p className={`text-xs ${theme.textMuted}`}>
        Applies to every account: {instrument.accounts.join(', ')}. The symbol cannot be changed.
      </p>
      <input value={form.sectype} onChange={set('sectype')} placeholder="Sectype" aria-label="Sectype" maxLength={7} className={field} />
      <input value={form.exchange} onChange={set('exchange')} placeholder="Exchange" aria-label="Exchange" className={field} />
      <input value={form.currency} onChange={set('currency')} placeholder="Currency" aria-label="Currency" maxLength={3} className={field} />
      {synthetic && (
        <>
          <select value={form.side} onChange={set('side')} className={field} aria-label="Side">
            <option value="">Side — TBD</option>
            <option value="BUY">BUY</option>
            <option value="SELL">SELL</option>
          </select>
          <p className={`text-[10px] ${theme.textMuted}`}>
            {missing.length
              ? `Synthetic. To transfer it to securities, fill: ${missing.join(', ')}.`
              : 'Synthetic, complete: it can be transferred to securities.'}
          </p>
        </>
      )}
      {conflicts && (
        <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
          <p className="font-bold">{instrument.symbol} {form.sectype.toUpperCase()} is already taken:</p>
          <ul className="mt-1 list-disc pl-4">
            {describeConflicts(conflicts).map((line) => (
              <li key={line} className={line.startsWith('OPEN POSITION') ? 'font-bold text-red-600' : ''}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${theme.textMuted} hover:bg-olive-50`}>
          CANCEL
        </button>
        {synthetic && !conflicts && (
          <button
            type="button"
            onClick={() => send(true)}
            disabled={saving || missing.length > 0}
            className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40"
          >
            SAVE & TRANSFER
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className={`rounded-lg px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40 ${
            conflicts ? 'bg-red-500' : 'bg-olive-500'
          }`}
        >
          {saving ? '…' : conflicts ? 'OVERWRITE' : 'SAVE'}
        </button>
      </div>
    </form>
  );
};

/**
 * Must stay at module scope: declared inside ScannerLibrary it becomes a new
 * component type on every render, and the remount mid-drag cancels the drag.
 */
const InstrumentCard = ({
  instrument, actions, draggable = false, dragged, setDragged, setDropFolderId,
  onSelect, selectedKey, folderName = null, selectable = false, checked = false, onToggle,
}) => (
  <div
    draggable={draggable}
    onDragStart={draggable ? (event) => {
      // Firefox only starts a drag when the payload is set.
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', instrumentKey(instrument));
      setDragged(instrument);
    } : undefined}
    onDragEnd={draggable ? () => { setDragged(null); setDropFolderId(null); } : undefined}
    className={`relative rounded-xl ${theme.cardBg} ${theme.cardBorder} p-2.5 ${
      draggable ? 'cursor-grab active:cursor-grabbing' : ''
    } ${dragged && instrumentKey(dragged) === instrumentKey(instrument) ? 'opacity-40' : ''} ${
      selectedKey === instrumentKey(instrument) ? 'ring-2 ring-olive-400' : ''
    } ${checked ? 'bg-olive-50 ring-2 ring-olive-300' : ''}`}
  >
    {selectable && (
      <label className="absolute right-2 top-2 z-10 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(instrument)}
          aria-label={`Select ${instrument.symbol}`}
          className="h-3.5 w-3.5 accent-olive-500"
        />
      </label>
    )}
    <button onClick={() => onSelect(instrument)} className="w-full text-left">
      <p className={`text-sm font-semibold ${theme.textSecondary} truncate`}>{instrument.symbol}</p>
      <p className={`text-[10px] ${theme.textMuted} truncate`}>
        {instrument.broker.toUpperCase()}
        {instrument.sectype ? ` · ${instrument.sectype}` : ''}
      </p>
      {/* In a folder the instrument is scanner-only, so which accounts carry it
          is noise — the folder is the whole answer. */}
      <p className={`text-[10px] ${theme.textMuted} truncate`} title={instrument.accounts.join(', ')}>
        {folderName ? `📁 ${truncateText(folderName, 18)}` : instrument.accounts.join(', ')}
      </p>
      <div className="mt-1 flex flex-wrap gap-1">
        <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${
          instrument.trading ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
        }`}>
          {instrument.trading ? 'TRADING' : 'SCANNER'}
        </span>
        {instrument.is_blacklisted && (
          <span className="inline-block rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold text-red-500">
            BLACKLIST
          </span>
        )}
        {!instrument.is_active && (
          <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
            INACTIVE
          </span>
        )}
      </div>
    </button>
    <div className="mt-2 flex flex-wrap gap-1.5">{actions}</div>
  </div>
);

/**
 * The auto-folder preview: one virtual account, one folder.
 *
 * Every instrument is a line the operator can uncheck, and a line that already
 * belongs to another folder starts unchecked — moving it is a decision, not a
 * side effect of pressing APPLY.
 */
const AutoFolderPreview = ({ proposals, chosen, toggleItem, toggleFolder }) => (
  <div className="max-h-96 space-y-4 overflow-y-auto">
    {proposals.length === 0 && (
      <p className={`text-xs ${theme.textMuted}`}>
        No virtual account has instruments to file. Create one, or enable its instruments first.
      </p>
    )}
    {proposals.map((proposal) => {
      const picked = chosen[proposal.name] || {};
      const pickedCount = proposal.items.filter((i) => picked[instrumentKey(i)]).length;
      return (
        <div key={proposal.name} className={`rounded-xl ${theme.cardBorder} p-3`}>
          <div className="flex items-center justify-between">
            <div>
              <h4 className={`text-sm font-bold ${theme.textPrimary}`}>📁 {proposal.name}</h4>
              <p className={`text-[10px] ${theme.textMuted}`}>
                {proposal.existing_folder_id
                  ? 'folder exists — it will be filled, not replaced'
                  : 'new folder'}
                {' · '}
                {pickedCount} of {proposal.items.length} selected
              </p>
            </div>
            <button
              onClick={() => toggleFolder(proposal)}
              className="rounded bg-olive-50 px-2 py-1 text-[10px] font-bold text-olive-600 hover:bg-olive-100"
            >
              {pickedCount === proposal.items.length ? 'NONE' : 'ALL'}
            </button>
          </div>
          <div className="mt-2 space-y-0.5">
            {proposal.items.map((item) => {
              const conflict = item.current_folder_id
                && item.current_folder_id !== proposal.existing_folder_id;
              return (
                <label
                  key={instrumentKey(item)}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-olive-50"
                >
                  <input
                    type="checkbox"
                    checked={!!picked[instrumentKey(item)]}
                    onChange={() => toggleItem(proposal.name, item)}
                    aria-label={`${proposal.name}: ${item.symbol}`}
                    className="h-3.5 w-3.5 accent-olive-500"
                  />
                  <span className={theme.textSecondary}>{item.symbol}</span>
                  <span className={`text-[10px] ${theme.textMuted}`}>
                    {item.broker.toUpperCase()}{item.sectype ? ` · ${item.sectype}` : ''}
                  </span>
                  {conflict && (
                    <span className="ml-auto rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-600">
                      now in {truncateText(item.current_folder_name || '—', 14)}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);

/**
 * COUNTER's instrument library.
 *
 * One card per instrument, whatever the accounts behind it. Folders are pure
 * grouping — everything inside one is scanner-only, and leaving a folder for a
 * trading account is an explicit, broker-checked step.
 */
const ScannerLibrary = ({ accounts = [], onSelect, selectedKey, onPromoted }) => {
  const [library, setLibrary] = useState({ folders: [], instruments: [], monitor_account: null });
  const [openFolderId, setOpenFolderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picker, setPicker] = useState(null);   // add-existing / parent / promote
  const [creatingSynthetic, setCreatingSynthetic] = useState(false);
  const [creatingPair, setCreatingPair] = useState(false);
  const [error, setError] = useState(null);
  const [dragged, setDragged] = useState(null);      // instrument being dragged
  const [dropFolderId, setDropFolderId] = useState(null);
  const [query, setQuery] = useState('');
  const [ascending, setAscending] = useState(true);
  const [blacklistView, setBlacklistView] = useState('clean');   // clean | blacklisted
  const [activeView, setActiveView] = useState('active');        // active | inactive
  const [selection, setSelection] = useState([]);    // keys of the checked cards
  const [autoPlan, setAutoPlan] = useState(null);    // { proposals, chosen } once loaded
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    const data = await fetchScannerLibrary();
    if (data) setLibrary(data);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const openFolder = library.folders.find((f) => f.id === openFolderId) || null;
  const visible = useCallback(
    (instruments) => sortInstruments(
      instruments.filter(
        (i) => matchesQuery(i, query)
          && matchesBlacklistView(i, blacklistView)
          // The blacklist is its own parked list; filtering it by active as
          // well would leave the operator staring at an empty screen.
          && (blacklistView === 'blacklisted' || matchesActiveView(i, activeView)),
      ),
      ascending,
    ),
    [query, ascending, blacklistView, activeView],
  );
  const pool = useMemo(() => visible(library.instruments), [visible, library.instruments]);
  const inFolder = useMemo(
    () => visible(library.instruments.filter((i) => i.folder_id === openFolderId)),
    [visible, library.instruments, openFolderId],
  );
  const outsideFolder = useMemo(
    () => visible(library.instruments.filter((i) => i.folder_id !== openFolderId)),
    [visible, library.instruments, openFolderId],
  );
  const sortedFolders = useMemo(() => {
    const sorted = [...library.folders].sort((a, b) => a.name.localeCompare(b.name));
    return ascending ? sorted : sorted.reverse();
  }, [library.folders, ascending]);
  const folderNames = useMemo(
    () => Object.fromEntries(library.folders.map((f) => [f.id, f.name])),
    [library.folders],
  );
  const folderCounts = useMemo(() => {
    const counts = {};
    library.instruments.forEach((i) => {
      if (i.folder_id) counts[i.folder_id] = (counts[i.folder_id] || 0) + 1;
    });
    return counts;
  }, [library.instruments]);

  // Selection is by key, so a reload that rebuilds the rows keeps it intact.
  const selected = useMemo(() => new Set(selection), [selection]);
  const selectedInstruments = useMemo(
    () => library.instruments.filter((i) => selected.has(instrumentKey(i))),
    [library.instruments, selected],
  );
  const blacklistableSelection = useMemo(
    () => selectedInstruments.filter((i) => i.is_blacklisted !== null
      && i.is_blacklisted !== undefined),
    [selectedInstruments],
  );
  const toggleSelected = (instrument) => setSelection((keys) => {
    const key = instrumentKey(instrument);
    return keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key];
  });

  const handleCreateFolder = async (name) => {
    const created = await createScannerFolder(name);
    if (!created) {
      setError('Could not create the folder (name already taken?)');
      return;
    }
    setPicker(null);
    await reload();
  };

  const handleRenameFolder = async (folder, name) => {
    const renamed = await renameScannerFolder(folder.id, name);
    if (!renamed) {
      setError('Could not rename the folder (name already taken?)');
      return;
    }
    setPicker(null);
    await reload();
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Instruments go back to the pool.`)) return;
    await deleteScannerFolder(folder.id);
    setOpenFolderId(null);
    await reload();
  };

  const handleCreateInstrument = async ({ folderId: pickedFolder, ...form }) => {
    const result = await createScannerInstrument({
      ...form,
      sectype: form.broker === 'okx' ? '' : form.sectype,
    });
    if (!result || result.error) {
      setError(String(result?.error || 'Could not create the instrument'));
      return;
    }
    const folderId = Number(pickedFolder) || null;
    // Filed straight away when a folder was picked; the parent question is only
    // raised when the instrument already lived on another account.
    const filed = folderId && await addInstrumentToFolder(folderId, result);
    if (filed?.needsParent) {
      setPicker({ kind: 'parent', instrument: result, folderId, accounts: filed.accounts });
    } else {
      setPicker(null);
    }
    await reload();
  };

  /**
   * The pair creator reports success without saying what it created, so the
   * library is re-read and whatever is new is what was just made.
   */
  const fileNewInstruments = async (folderId) => {
    const known = new Set(library.instruments.map(instrumentKey));
    const data = await fetchScannerLibrary();
    for (const created of (data?.instruments || [])) {
      if (folderId && !known.has(instrumentKey(created))) {
        // eslint-disable-next-line no-await-in-loop
        await addInstrumentToFolder(folderId, created);
      }
    }
    await reload();
  };

  const handleDeleteInstrument = async (instrument) => {
    if (!window.confirm(
      `Delete ${instrument.symbol} from every account that carries it? This cannot be undone.`,
    )) return;
    const result = await deleteScannerInstrument(instrument);
    if (!result || result.error) {
      setError(String(result?.error || 'Could not delete the instrument'));
      return;
    }
    setSelection((keys) => keys.filter((key) => key !== instrumentKey(instrument)));
    await reload();
  };

  // A sectype change changes the card's key, so the old selection is dropped.
  const handleEditInstrument = async (instrument, changes, overwrite, transfer) => {
    const result = await editScannerInstrument(instrument, changes, overwrite, transfer);
    if (result?.conflicts) return result;
    if (!result || result.error) {
      setError(String(result?.error || 'Could not edit the instrument'));
      return result;
    }
    setPicker(null);
    setSelection((keys) => keys.filter((key) => key !== instrumentKey(instrument)));
    await reload();
    return result;
  };

  /**
   * Move a batch into one folder.
   *
   * The parent question is per instrument and the server only raises it when it
   * has to, so the ones that need an answer are queued and asked one by one
   * instead of blocking the whole batch on the first of them.
   */
  const addManyToFolder = async (instruments, folderId, parentAccount = null) => {
    const pending = [];
    setBusy(true);
    for (const instrument of instruments) {
      // Sequential on purpose: the handler rewrites shared sync state per
      // instrument, and a parallel burst would interleave those writes.
      // eslint-disable-next-line no-await-in-loop
      const result = await addInstrumentToFolder(folderId, instrument, parentAccount);
      if (result?.needsParent) pending.push({ instrument, accounts: result.accounts });
    }
    setBusy(false);
    setSelection((keys) => keys.filter(
      (key) => pending.some((p) => instrumentKey(p.instrument) === key),
    ));
    if (pending.length) {
      // Seeding overwrites the other accounts, so the source is the operator's call.
      setPicker({
        kind: 'parent',
        instrument: pending[0].instrument,
        folderId,
        accounts: pending[0].accounts,
        queue: pending.slice(1),
      });
    } else {
      setPicker(null);
    }
    await reload();
  };

  const addToFolder = (instrument, folderId, parentAccount = null) => (
    addManyToFolder([instrument], folderId, parentAccount)
  );

  // Answering for one instrument moves the queue on to the next one that asked.
  const answerParent = async (account) => {
    const { instrument, folderId, queue = [] } = picker;
    await addInstrumentToFolder(folderId, instrument, account);
    setSelection((keys) => keys.filter((key) => key !== instrumentKey(instrument)));
    if (queue.length) {
      setPicker({
        kind: 'parent', folderId, instrument: queue[0].instrument,
        accounts: queue[0].accounts, queue: queue.slice(1),
      });
    } else {
      setPicker(null);
    }
    await reload();
  };

  // Dropping an instrument on a folder tile is the same move as the "→ FOLDER"
  // picker. Dragging a card that is part of the selection takes the whole
  // selection with it — what is checked is what moves.
  const handleDrop = async (folder) => {
    const instrument = dragged;
    setDragged(null);
    setDropFolderId(null);
    if (!instrument) return;
    const batch = selected.has(instrumentKey(instrument)) ? selectedInstruments : [instrument];
    const moving = batch.filter((i) => i.folder_id !== folder.id);
    if (!moving.length) return;
    await addManyToFolder(moving, folder.id);
  };

  /**
   * Both flags are set for the whole card, and the server owns the coupling
   * between them — the reload is what tells the operator where the instrument
   * ended up, rather than a guess painted on before the answer arrives.
   */
  const applyFlag = async (instruments, call, value) => {
    setBusy(true);
    const failures = [];
    for (const instrument of instruments) {
      // eslint-disable-next-line no-await-in-loop
      const result = await call(instrument, value);
      if (!result || result.error) failures.push(instrument.symbol);
    }
    setBusy(false);
    if (failures.length) setError(`Could not change: ${failures.join(', ')}`);
    setSelection([]);
    await reload();
  };

  const setBlacklist = (instruments, value) => (
    applyFlag(instruments, setInstrumentBlacklist, value)
  );
  const setActive = (instruments, value) => applyFlag(instruments, setInstrumentActive, value);

  const promote = async (instrument, account) => {
    setError(null);
    const result = await promoteInstrumentToTrading(instrument, account);
    if (!result || result.error) {
      setError(String(result?.error || 'Could not send the instrument to trading'));
    } else if (!result.needsTransfer) {
      onPromoted?.(instrument.broker, account);
    }
    setPicker(result?.needsTransfer ? { kind: 'transfer', syntheticId: result.syntheticId, account } : null);
    await reload();
  };

  const openAutoFolders = async () => {
    setBusy(true);
    const plan = await fetchAutoFolderPlan();
    setBusy(false);
    if (!plan) {
      setError('Could not read the virtual accounts');
      return;
    }
    // A clean instrument is checked; one that already sits elsewhere is not —
    // the operator opts into every move that takes something out of a folder.
    const chosen = {};
    plan.proposals.forEach((proposal) => {
      chosen[proposal.name] = Object.fromEntries(proposal.items.map((item) => [
        instrumentKey(item),
        !item.current_folder_id || item.current_folder_id === proposal.existing_folder_id,
      ]));
    });
    setAutoPlan({ proposals: plan.proposals, chosen });
  };

  const toggleAutoItem = (name, item) => setAutoPlan((plan) => ({
    ...plan,
    chosen: {
      ...plan.chosen,
      [name]: { ...plan.chosen[name], [instrumentKey(item)]: !plan.chosen[name][instrumentKey(item)] },
    },
  }));

  const toggleAutoFolder = (proposal) => setAutoPlan((plan) => {
    const picked = plan.chosen[proposal.name];
    const all = proposal.items.every((item) => picked[instrumentKey(item)]);
    return {
      ...plan,
      chosen: {
        ...plan.chosen,
        [proposal.name]: Object.fromEntries(
          proposal.items.map((item) => [instrumentKey(item), !all]),
        ),
      },
    };
  });

  const applyAutoFolders = async () => {
    const folders = autoPlan.proposals
      .map((proposal) => ({
        name: proposal.name,
        items: proposal.items
          .filter((item) => autoPlan.chosen[proposal.name][instrumentKey(item)])
          .map(({ broker, symbol, sectype, parent_account: parentAccount }) => ({
            broker, symbol, sectype, parent_account: parentAccount,
          })),
        empty: proposal.items.length === 0,
      }))
      // Unchecking every instrument is how a proposal is turned down. A virtual
      // account that had nothing to offer in the first place still earns its
      // folder — the layout is the ask there, not the contents.
      .filter((entry) => entry.items.length > 0 || entry.empty)
      .map(({ name, items }) => ({ name, items }));
    setBusy(true);
    const result = await applyAutoFolderPlan(folders);
    setBusy(false);
    if (!result) {
      setError('Could not apply the auto-folder plan');
      return;
    }
    if (result.failed?.length) {
      setError(`${result.failed.length} instrument(s) could not be filed: ${
        result.failed.map((f) => f.symbol || f.folder).join(', ')}`);
    }
    setAutoPlan(null);
    await reload();
  };

  // OKX is one account per user and everything addresses it by the username,
  // so the picker names it that way too rather than by the linked account.
  const username = (library.monitor_account || '').replace('MONITOR:', '');
  // The pair creator's legs: what the library already knows, filtered the way
  // the creator filters a fetched list. MONITOR has no positions endpoint to ask.
  const pairLegs = useMemo(
    () => library.instruments
      .filter((i) => i.broker === 'ib' && i.sectype !== 'SYNTH'
        && i.is_active !== false && i.is_blacklisted !== true)
      .map((i) => ({ symbol: i.symbol, sectype: i.sectype })),
    [library.instruments],
  );

  const ibAccounts = useMemo(() => {
    const linked = accounts.filter((a) => a.broker === 'ib').map((a) => a.account);
    return [library.monitor_account, ...linked].filter(Boolean);
  }, [accounts, library.monitor_account]);

  const compatibleAccounts = (instrument) => accounts
    .filter((a) => a.broker === instrument.broker && a.broker !== 'virtual')
    .map((a) => (a.broker === 'okx' && username ? { ...a, account: username } : a));

  const actionButton = (label, onClick, tone = 'olive') => (
    <button
      onClick={onClick}
      className={`shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-bold ${
        tone === 'red' ? 'bg-red-50 text-red-500 hover:bg-red-100'
          : 'bg-olive-50 text-olive-600 hover:bg-olive-100'
      }`}
    >
      {label}
    </button>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className={`h-6 w-6 animate-spin rounded-full border-2 ${theme.spinner}`} />
      </div>
    );
  }

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by symbol"
        aria-label="Search by symbol"
        className="w-48 rounded-lg border border-olive-200 px-3 py-1.5 text-sm outline-none focus:border-olive-400"
      />
      {query && (
        <button onClick={() => setQuery('')} className={`text-xs font-bold ${theme.textMuted}`}>
          CLEAR
        </button>
      )}
      <button
        onClick={() => setAscending((value) => !value)}
        title="Sort alphabetically"
        className="rounded-lg bg-olive-50 px-2.5 py-1.5 text-[11px] font-bold text-olive-600 hover:bg-olive-100"
      >
        {ascending ? 'A → Z' : 'Z → A'}
      </button>
      {!openFolder && (
        <>
          <button
            onClick={() => setPicker({ kind: 'newInstrument' })}
            title="scanner-only until you send it to trading"
            className={`h-8 shrink-0 whitespace-nowrap rounded-lg border border-dashed border-olive-200 px-2.5 text-[11px] font-bold ${theme.textMuted} hover:border-olive-400`}
          >
            + NEW INSTRUMENT
          </button>
          <button
            onClick={() => setCreatingPair(true)}
            title="two legs, one synthetic symbol"
            className={`h-8 shrink-0 whitespace-nowrap rounded-lg border border-dashed border-olive-200 px-2.5 text-[11px] font-bold ${theme.textMuted} hover:border-olive-400`}
          >
            + NEW PAIR
          </button>
        </>
      )}
      <div className="flex overflow-hidden rounded-lg border border-olive-200">
        {[['clean', 'NOT BLACKLISTED'], ['blacklisted', 'BLACKLISTED']].map(([value, label]) => (
          <button
            key={value}
            onClick={() => setBlacklistView(value)}
            className={`px-2.5 py-1.5 text-[11px] font-bold ${
              blacklistView === value
                ? 'bg-olive-500 text-white'
                : `${theme.textMuted} hover:bg-olive-50`
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className={`flex h-8 shrink-0 overflow-hidden rounded-lg border border-olive-200 ${
        blacklistView === 'blacklisted' ? 'hidden' : ''
      }`}>
        {[['active', 'ACTIVE'], ['inactive', 'INACTIVE']].map(([value, label]) => (
          <button
            key={value}
            onClick={() => setActiveView(value)}
            className={`px-2.5 py-1.5 text-[11px] font-bold ${
              activeView === value
                ? 'bg-olive-500 text-white'
                : `${theme.textMuted} hover:bg-olive-50`
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  // Only what carries a blacklist of its own can be put on one; a synthetic or
  // a pair has no such column, so the button would write nowhere.
  const flagActions = (instrument) => (
    <>
      {instrument.is_blacklisted === true && actionButton(
        'UNBLACKLIST', () => setBlacklist([instrument], false),
      )}
      {instrument.is_blacklisted === false && actionButton(
        'BLACKLIST', () => setBlacklist([instrument], true), 'red',
      )}
      {actionButton(
        instrument.is_active ? 'DEACTIVATE' : 'ACTIVATE',
        () => setActive([instrument], !instrument.is_active),
        instrument.is_active ? 'red' : 'olive',
      )}
    </>
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-600">
          {error}
          <button className="ml-2 font-bold" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {!openFolder && (
        <>
          {toolbar}

          {selection.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-olive-300 bg-olive-50 p-2.5">
              <span className={`text-xs font-bold ${theme.textPrimary}`}>
                {selection.length} selected
              </span>
              {library.folders.length > 0 && actionButton(
                `→ FOLDER (${selection.length})`,
                () => setPicker({ kind: 'folder', instruments: selectedInstruments }),
              )}
              {blacklistView === 'clean'
                ? actionButton(`BLACKLIST (${selection.length})`,
                  () => setBlacklist(blacklistableSelection, true), 'red')
                : actionButton(`UNBLACKLIST (${selection.length})`,
                  () => setBlacklist(blacklistableSelection, false))}
              {actionButton('ACTIVATE', () => setActive(selectedInstruments, true))}
              {actionButton('DEACTIVATE', () => setActive(selectedInstruments, false), 'red')}
              {selectedInstruments.some((i) => i.broker === 'ib') && actionButton('→ ACCOUNT',
                () => setPicker({ kind: 'accounts', instruments: selectedInstruments.filter((i) => i.broker === 'ib') }))}
              {actionButton('SELECT NONE', () => setSelection([]))}
              {busy && <span className={`text-[10px] ${theme.textMuted}`}>working…</span>}
              <ul aria-label="Selected instruments" className="flex min-w-0 basis-full flex-wrap gap-1.5">
                {selectedInstruments.map((instrument) => (
                  <li
                    key={instrumentKey(instrument)}
                    className={`min-w-0 max-w-full rounded-md border border-olive-200 bg-white px-2 py-1 text-xs ${theme.textPrimary}`}
                  >
                    <span className="break-all font-semibold">{instrument.symbol}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {query.trim().length > 0 && (
            <div>
              <h3 className={`mb-2 text-xs font-bold uppercase tracking-wide ${theme.textMuted}`}>
                All instruments ({pool.length} of {library.instruments.length})
                {library.folders.length > 0 && (
                  <span className="ml-2 font-normal normal-case tracking-normal">
                    — drag a card onto a folder to add it
                  </span>
                )}
              </h3>
              {pool.length === 0 ? (
                <div className={`rounded-xl ${theme.cardBg} ${theme.cardBorder} p-6 text-center text-sm ${theme.textMuted}`}>
                  {`Nothing matches "${query}".`}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
                  {pool.map((instrument) => (
                    <InstrumentCard
                      key={instrumentKey(instrument)}
                      instrument={instrument}
                      draggable={library.folders.length > 0}
                      folderName={folderNames[instrument.folder_id] || null}
                      dragged={dragged}
                      setDragged={setDragged}
                      setDropFolderId={setDropFolderId}
                      onSelect={onSelect}
                      selectedKey={selectedKey}
                      selectable
                      checked={selected.has(instrumentKey(instrument))}
                      onToggle={toggleSelected}
                      actions={
                        <>
                          {library.folders.length > 0 && actionButton('→ FOLDER', () => setPicker({
                            kind: 'folder', instruments: [instrument],
                          }))}
                          {actionButton('→ TRADE', () => setPicker({ kind: 'promote', instrument }))}
                          {instrument.broker === 'ib' && actionButton('ACCOUNTS', () => setPicker({ kind: 'accounts', instruments: [instrument] }))}
                          {isEditable(instrument) && actionButton('EDIT', () => setPicker({ kind: 'edit', instrument }))}
                          {flagActions(instrument)}
                          {actionButton('DELETE', () => handleDeleteInstrument(instrument), 'red')}
                        </>
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {sortedFolders.map((folder) => (
              <div
                key={folder.id}
                onDragOver={(event) => {
                  if (!dragged) return;
                  event.preventDefault();          // without this the drop never fires
                  event.dataTransfer.dropEffect = 'move';
                  setDropFolderId(folder.id);
                }}
                onDragLeave={() => setDropFolderId((id) => (id === folder.id ? null : id))}
                onDrop={(event) => { event.preventDefault(); handleDrop(folder); }}
                className={`w-44 min-w-0 max-w-full rounded-xl ${theme.cardBg} ${theme.cardBorder} p-2.5 transition-colors ${
                  dropFolderId === folder.id ? 'border-olive-400 bg-olive-50 ring-2 ring-olive-300' : ''
                }`}
              >
                <button onClick={() => setOpenFolderId(folder.id)} className="w-full text-left">
                  <h3 title={folder.name} className={`truncate text-xs font-bold ${theme.textPrimary}`}>
                    {folder.name}
                  </h3>
                  <p className={`text-[10px] ${theme.textMuted}`}>
                    {folderCounts[folder.id] || 0} instruments · scanner only
                  </p>
                </button>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {actionButton('RENAME', () => setPicker({ kind: 'rename', folder }))}
                  {actionButton('DELETE', () => handleDeleteFolder(folder), 'red')}
                </div>
              </div>
            ))}
            <button
              onClick={() => setPicker({ kind: 'newFolder' })}
              className={`w-44 max-w-full rounded-xl border border-dashed border-olive-200 p-2.5 text-xs font-bold ${theme.textMuted} hover:border-olive-400`}
            >
              + NEW FOLDER
            </button>
            <button
              onClick={openAutoFolders}
              className={`w-44 max-w-full rounded-xl border border-dashed border-olive-200 p-2.5 text-xs font-bold ${theme.textMuted} hover:border-olive-400`}
            >
              ⚙ AUTO-FOLDERS
              <span className={`mt-0.5 block text-[10px] font-normal ${theme.textMuted}`}>
                one folder per virtual account
              </span>
            </button>
          </div>
        </>
      )}

      {openFolder && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setOpenFolderId(null)} className={`text-xs font-bold ${theme.textMuted}`}>
              ← LIBRARY
            </button>
            <h3 className={`text-sm font-bold ${theme.textPrimary}`}>{openFolder.name}</h3>
            {actionButton('+ EXISTING', () => setPicker({ kind: 'add', folderId: openFolder.id }))}
            {actionButton('+ SYNTHETIC', () => setCreatingSynthetic(true))}
            {actionButton('+ PAIR', () => setCreatingPair(true))}
          </div>

          {toolbar}

          {inFolder.length === 0 ? (
            <div className={`rounded-xl ${theme.cardBg} ${theme.cardBorder} p-6 text-center text-sm ${theme.textMuted}`}>
              {query
                ? `Nothing in this folder matches "${query}".`
                : 'Empty folder. Add an existing instrument or create a synthetic one.'}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
              {inFolder.map((instrument) => (
                <InstrumentCard
                  key={instrumentKey(instrument)}
                  instrument={instrument}
                  folderName={openFolder.name}
                  onSelect={onSelect}
                  selectedKey={selectedKey}
                  actions={
                    <>
                      {actionButton('→ TRADE', () => setPicker({ kind: 'promote', instrument }))}
                      {instrument.broker === 'ib' && actionButton('ACCOUNTS', () => setPicker({ kind: 'accounts', instruments: [instrument] }))}
                      {actionButton('REMOVE', async () => {
                        await removeInstrumentFromFolder(openFolder.id, instrument);
                        await reload();
                      }, 'red')}
                      {isEditable(instrument) && actionButton('EDIT', () => setPicker({ kind: 'edit', instrument }))}
                      {flagActions(instrument)}
                      {actionButton('DELETE', () => handleDeleteInstrument(instrument), 'red')}
                    </>
                  }
                />
              ))}
            </div>
          )}
        </>
      )}

      {picker?.kind === 'accounts' && (
        <AccountInstrumentsModal library={library} accounts={accounts} instruments={picker.instruments}
          onClose={() => setPicker(null)} onChanged={reload} />
      )}

      {picker?.kind === 'newFolder' && (
        <Modal title="New folder" onClose={() => setPicker(null)}>
          <FolderNameForm
            submitLabel="CREATE"
            onSubmit={handleCreateFolder}
            onClose={() => setPicker(null)}
          />
        </Modal>
      )}

      {picker?.kind === 'newInstrument' && (
        <Modal title="New instrument" onClose={() => setPicker(null)}>
          <InstrumentForm
            ibAccounts={ibAccounts}
            hasOkx={accounts.some((a) => a.broker === 'okx')}
            defaultAccount={library.monitor_account}
            folders={sortedFolders}
            defaultFolderId={openFolderId}
            onSubmit={handleCreateInstrument}
            onClose={() => setPicker(null)}
          />
        </Modal>
      )}

      {picker?.kind === 'edit' && (
        <Modal title={`Edit ${picker.instrument.symbol}`} onClose={() => setPicker(null)}>
          <EditInstrumentForm
            instrument={picker.instrument}
            onSubmit={(changes, overwrite, transfer) => (
              handleEditInstrument(picker.instrument, changes, overwrite, transfer)
            )}
            onClose={() => setPicker(null)}
          />
        </Modal>
      )}

      {picker?.kind === 'rename' && (
        <Modal title={`Rename "${picker.folder.name}"`} onClose={() => setPicker(null)}>
          <FolderNameForm
            initialName={picker.folder.name}
            submitLabel="SAVE"
            onSubmit={(name) => handleRenameFolder(picker.folder, name)}
            onClose={() => setPicker(null)}
          />
        </Modal>
      )}

      {picker?.kind === 'add' && (
        <Modal title="Add an existing instrument" onClose={() => setPicker(null)}>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {outsideFolder.map((instrument) => (
              <button
                key={instrumentKey(instrument)}
                onClick={() => addToFolder(instrument, picker.folderId)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-olive-50"
              >
                <span className={theme.textSecondary}>{instrument.symbol}</span>
                <span className={`text-[10px] ${theme.textMuted}`}>
                  {instrument.broker.toUpperCase()} {instrument.sectype}
                </span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {picker?.kind === 'folder' && (
        <Modal
          title={picker.instruments.length === 1
            ? `Move ${picker.instruments[0].symbol} to a folder`
            : `Move ${picker.instruments.length} instruments to a folder`}
          onClose={() => setPicker(null)}
        >
          <div className="space-y-1">
            {sortedFolders.map((folder) => (
              <button
                key={folder.id}
                onClick={() => addManyToFolder(picker.instruments, folder.id)}
                className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-olive-50"
              >
                {folder.name}
              </button>
            ))}
          </div>
        </Modal>
      )}

      {picker?.kind === 'parent' && (
        <ParentPicker
          instrument={picker.instrument}
          accounts={picker.accounts}
          remaining={picker.queue?.length || 0}
          onPick={answerParent}
          onClose={() => setPicker(null)}
        />
      )}

      {picker?.kind === 'promote' && (
        <Modal title={`Send ${picker.instrument.symbol} to trading`} onClose={() => setPicker(null)}>
          {compatibleAccounts(picker.instrument).length === 0 ? (
            <p className={`text-xs ${theme.textMuted}`}>
              No {picker.instrument.broker.toUpperCase()} account linked.
            </p>
          ) : (
            <div className="space-y-1">
              {compatibleAccounts(picker.instrument).map((account) => (
                <button
                  key={account.id}
                  onClick={() => promote(picker.instrument, account.account)}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-olive-50"
                >
                  <span className={theme.textSecondary}>{account.account}</span>
                  <span className={`text-[10px] ${theme.textMuted}`}>{account.broker.toUpperCase()}</span>
                </button>
              ))}
            </div>
          )}
        </Modal>
      )}

      {picker?.kind === 'transfer' && (
        <SyntheticTransferModal
          syntheticId={picker.syntheticId}
          onClose={() => setPicker(null)}
          onTransferred={(instrument) => promote(instrument, picker.account)}
        />
      )}

      {autoPlan && (
        <Modal title="Folders from virtual accounts" onClose={() => setAutoPlan(null)} wide>
          <AutoFolderPreview
            proposals={autoPlan.proposals}
            chosen={autoPlan.chosen}
            toggleItem={toggleAutoItem}
            toggleFolder={toggleAutoFolder}
          />
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => setAutoPlan(null)}
              className={`rounded-lg px-3 py-1.5 text-xs font-bold ${theme.textMuted} hover:bg-olive-50`}
            >
              CANCEL
            </button>
            <button
              onClick={applyAutoFolders}
              disabled={busy || autoPlan.proposals.length === 0}
              className="rounded-lg bg-olive-500 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-40"
            >
              {busy ? '…' : 'APPLY'}
            </button>
          </div>
        </Modal>
      )}

      {creatingPair && (
        // Renders its own overlay, so it is not wrapped in Modal. Created on
        // MONITOR; inside a folder the new pair is filed there right away.
        <SynthPairCreator
          selectedAccount={{ broker: 'ib', account: library.monitor_account }}
          assets={pairLegs}
          onPairCreated={() => fileNewInstruments(openFolderId)}
          onClose={() => setCreatingPair(false)}
        />
      )}

      {creatingSynthetic && openFolder && (
        <Modal title="New synthetic instrument" onClose={() => setCreatingSynthetic(false)}>
          <SyntheticAssetCreator
            selectedAccount={{ broker: 'ib', account: library.monitor_account }}
            onAssetCreated={async (asset) => {
              // Created on MONITOR; the folder it was created in owns it right away.
              // Read the row back rather than trusting the form: the server fills
              // in the sectype the membership key is built from.
              const data = await fetchScannerLibrary();
              const created = data?.instruments.find(
                (i) => i.broker === 'ib' && i.symbol === asset?.symbol,
              );
              if (created) await addInstrumentToFolder(openFolder.id, created);
              setCreatingSynthetic(false);
              await reload();
            }}
            onClose={() => setCreatingSynthetic(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default ScannerLibrary;
